// Version V1 corrigée pour le système unifié demi-journée
import { FC, Fragment, HTMLAttributes, useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { SubmitHandler, useForm } from "react-hook-form";
import Input from "shared/Input/Input";
import Select from "shared/Select/Select";
import { z } from "zod";
import FormItem from "./FormItem";
import Multiselect from "multiselect-react-dropdown";
import { Switch } from "@mui/material";
import { ErrorMessage } from "../ErrorMessages";
import { LocationFormProps } from "../interfaces";
import { DAYS_OF_WEEK, formSchema } from "../utlis";
import toast from "react-hot-toast";

interface PricingCategory {
  id: string;
  title: string;
  description: string;
  emoji: string;
  colorClass: string;
  enabled: boolean;
  isConfigOpen: boolean;
}

const LocationAvailibility: FC<LocationFormProps & HTMLAttributes<HTMLDivElement>> = ({
  isLoading,
  handleForm,
  data,
  FormData,
}) => {
  const eventsList = ["Concert", "Conférence", "Mariage", "Anniversaire", "Séminaire"];

  const [refreshKey, setRefreshKey] = useState(0); // Pour forcer le re-render
  const [halfdayOptions, setHalfdayOptions] = useState({
    morning: true,
    afternoon: false,
  });

  // Fonction pour répliquer les valeurs matin sur tous les jours
  const applyToAllMorning = (sourceDay: string) => {
    const currentPrices = getValues("prices") || [];
    const sourceDayLabel = DAYS_OF_WEEK.find((d) => d.value === sourceDay)?.label;

    // Récupérer les valeurs actuelles du DOM pour le jour source
    const sourceMorningRow = document.querySelector(`[data-halfday-type="morning"][data-day="${sourceDay}"]`);
    if (!sourceMorningRow) {
      toast.error("Ligne source introuvable");
      return;
    }

    // Récupérer les valeurs du DOM (incluant les modifications non sauvegardées)
    const startHour =
      (sourceMorningRow.querySelector('[data-field="start"]') as HTMLSelectElement)?.value || "08:00";
    const endHour = (sourceMorningRow.querySelector('[data-field="end"]') as HTMLSelectElement)?.value || "14:00";
    const priceValue = Number(
      (sourceMorningRow.querySelector('[data-field="price"]') as HTMLInputElement)?.value || "0"
    );
    const capacityValue = Number(
      (sourceMorningRow.querySelector('[data-field="capacity"]') as HTMLInputElement)?.value || "1"
    );

    // Trouver la configuration source pour obtenir les autres champs
    let sourceMorningPrice = currentPrices.find(
      (p) => p.selectedDay === sourceDayLabel && p.type === "demi-journée" && p.startHour === "08:00"
    );

    // Si pas de configuration existante, créer une base
    if (!sourceMorningPrice) {
      sourceMorningPrice = {
        price: priceValue,
        min: 1,
        max: capacityValue,
        startHour: startHour,
        endHour: endHour,
        selectedDay: sourceDayLabel!,
        type: "demi-journée" as const,
      };
    } else {
      // Mettre à jour avec les valeurs du DOM
      sourceMorningPrice = {
        ...sourceMorningPrice,
        price: priceValue,
        max: capacityValue,
        startHour: startHour,
        endHour: endHour,
      };
    }

    // Appliquer aux autres jours (matin uniquement)
    const updatedPrices = [...currentPrices];

    DAYS_OF_WEEK.filter((day) => day.value !== sourceDay).forEach((day) => {
      if (halfdayOptions.morning) {
        // Chercher si un prix matin existe déjà pour ce jour
        const existingIndex = updatedPrices.findIndex(
          (p) => p.selectedDay === day.label && p.type === "demi-journée" && p.startHour === "08:00"
        );

        const newMorningPrice = {
          ...sourceMorningPrice!,
          selectedDay: day.label,
        };

        if (existingIndex >= 0) {
          // Remplacer le prix existant
          updatedPrices[existingIndex] = newMorningPrice;
        } else {
          // Ajouter un nouveau prix
          updatedPrices.push(newMorningPrice);
        }
      }
    });

    // Mettre à jour le formulaire
    setValue("prices", updatedPrices);
    trigger("prices");
    setRefreshKey((prev) => prev + 1);

    toast.success("Paramètres matin appliqués à tous les jours !");
  };

  // Fonction pour répliquer les valeurs après-midi sur tous les jours
  const applyToAllAfternoon = (sourceDay: string) => {
    const currentPrices = getValues("prices") || [];
    const sourceDayLabel = DAYS_OF_WEEK.find((d) => d.value === sourceDay)?.label;

    // Récupérer les valeurs actuelles du DOM pour le jour source
    const sourceAfternoonRow = document.querySelector(`[data-halfday-type="afternoon"][data-day="${sourceDay}"]`);
    if (!sourceAfternoonRow) {
      toast.error("Ligne source introuvable");
      return;
    }

    // Récupérer les valeurs du DOM (incluant les modifications non sauvegardées)
    const startHour =
      (sourceAfternoonRow.querySelector('[data-field="start"]') as HTMLSelectElement)?.value || "14:00";
    const endHour =
      (sourceAfternoonRow.querySelector('[data-field="end"]') as HTMLSelectElement)?.value || "20:00";
    const priceValue = Number(
      (sourceAfternoonRow.querySelector('[data-field="price"]') as HTMLInputElement)?.value || "0"
    );
    const capacityValue = Number(
      (sourceAfternoonRow.querySelector('[data-field="capacity"]') as HTMLInputElement)?.value || "1"
    );

    // Trouver la configuration source pour obtenir les autres champs
    let sourceAfternoonPrice = currentPrices.find(
      (p) => p.selectedDay === sourceDayLabel && p.type === "demi-journée" && p.startHour === "14:00"
    );

    // Si pas de configuration existante, créer une base
    if (!sourceAfternoonPrice) {
      sourceAfternoonPrice = {
        price: priceValue,
        min: 1,
        max: capacityValue,
        startHour: startHour,
        endHour: endHour,
        selectedDay: sourceDayLabel!,
        type: "demi-journée" as const,
      };
    } else {
      // Mettre à jour avec les valeurs du DOM
      sourceAfternoonPrice = {
        ...sourceAfternoonPrice,
        price: priceValue,
        max: capacityValue,
        startHour: startHour,
        endHour: endHour,
      };
    }

    // Appliquer aux autres jours (après-midi uniquement)
    const updatedPrices = [...currentPrices];

    DAYS_OF_WEEK.filter((day) => day.value !== sourceDay).forEach((day) => {
      if (halfdayOptions.afternoon) {
        // Chercher si un prix après-midi existe déjà pour ce jour
        const existingIndex = updatedPrices.findIndex(
          (p) => p.selectedDay === day.label && p.type === "demi-journée" && p.startHour === "14:00"
        );

        const newAfternoonPrice = {
          ...sourceAfternoonPrice!,
          selectedDay: day.label,
        };

        if (existingIndex >= 0) {
          // Remplacer le prix existant
          updatedPrices[existingIndex] = newAfternoonPrice;
        } else {
          // Ajouter un nouveau prix
          updatedPrices.push(newAfternoonPrice);
        }
      }
    });

    // Mettre à jour le formulaire
    setValue("prices", updatedPrices);
    trigger("prices");
    setRefreshKey((prev) => prev + 1);

    toast.success("Paramètres après-midi appliqués à tous les jours !");
  };
  const [pricingCategories, setPricingCategories] = useState<PricingCategory[]>([
    {
      id: "hourly",
      title: "Tarifs à l'heure",
      description: `Minimum ${data?.nbreHour || 2} heures`,
      emoji: "🕐",
      colorClass: "blue",
      enabled: true, // Activé par défaut
      isConfigOpen: false,
    },
    {
      id: "evening",
      title: "Soirée événementiel",
      description: "Événements de soirée",
      emoji: "🌙",
      colorClass: "purple",
      enabled: false,
      isConfigOpen: false,
    },
    {
      id: "halfday",
      title: "Demi-journée",
      description: "Matin et/ou après-midi",
      emoji: "🌗",
      colorClass: "green",
      enabled: false,
      isConfigOpen: false,
    },
    {
      id: "fullday",
      title: "Journée complète",
      description: "Toute la journée",
      emoji: "☀️",
      colorClass: "orange",
      enabled: false,
      isConfigOpen: false,
    },
  ]);

  const getColorClasses = (colorClass: string) => {
    const colorMap = {
      blue: {
        bgIcon: "bg-blue-100 dark:bg-blue-900/30",
        bgButton: "bg-blue-50 dark:bg-blue-900/30",
        textButton: "text-blue-700 dark:text-blue-300",
        hoverButton: "hover:bg-blue-100 dark:hover:bg-blue-900/50",
      },
      purple: {
        bgIcon: "bg-purple-100 dark:bg-purple-900/30",
        bgButton: "bg-purple-50 dark:bg-purple-900/30",
        textButton: "text-purple-700 dark:text-purple-300",
        hoverButton: "hover:bg-purple-100 dark:hover:bg-purple-900/50",
      },
      green: {
        bgIcon: "bg-green-100 dark:bg-green-900/30",
        bgButton: "bg-green-50 dark:bg-green-900/30",
        textButton: "text-green-700 dark:text-green-300",
        hoverButton: "hover:bg-green-100 dark:hover:bg-green-900/50",
      },
      orange: {
        bgIcon: "bg-orange-100 dark:bg-orange-900/30",
        bgButton: "bg-orange-50 dark:bg-orange-900/30",
        textButton: "text-orange-700 dark:text-orange-300",
        hoverButton: "hover:bg-orange-100 dark:hover:bg-orange-900/50",
      },
    };
    return colorMap[colorClass as keyof typeof colorMap];
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
    setValue,
    trigger,
  } = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      reservation_type: data?.reservation_type ?? undefined,
      prices: [],
      cleaning_costs: data?.cleaning_costs ?? 0,
      cleaning_costs_events: data?.cleaning_costs_events ?? [],
      guarantee: data?.guarantee ?? 0,
    },
    mode: "onSubmit",
  });

  useEffect(() => {
    if (data) {
      // Only set reservation_type if it exists and is not empty
      if (data.reservation_type && data.reservation_type.trim() !== "") {
        setValue("reservation_type", data.reservation_type);
      }
      setValue("cleaning_costs", data.cleaning_costs ?? 0);
      setValue("cleaning_costs_events", data.cleaning_costs_events ?? []);
      setValue("guarantee", data.guarantee ?? 0);

      // Si des prix existent déjà, les charger
      if (data.prices && data.prices.length > 0) {
        setValue("prices", data.prices as any);
      } else {
        // Générer des prix par défaut pour la catégorie horaire

        const defaultPrices = DAYS_OF_WEEK.map((day) => ({
          price: 0,
          min: 1,
          max: (data as any)?.visitor || 50,
          startHour: "09:00",
          endHour: "18:00",
          selectedDay: day.label,
          type: "heure" as "heure" | "soirée" | "demi-journée" | "journée complète",
        }));
        setValue("prices", defaultPrices);
      }
    }
  }, [data]);

  useEffect(() => {
    if (handleForm.type === "chekLocationFormSix") {
      handleSubmit(onSubmit)();
    }
  }, [handleForm]);

  const onSubmit: SubmitHandler<z.infer<typeof formSchema>> = (data) => {
    // Validation stricte : au moins une catégorie doit être activée
    const hasEnabledCategories = pricingCategories.some((cat) => cat.enabled);
    if (!hasEnabledCategories) {
      toast.error("Vous devez activer au moins un type de tarification (horaire, soirée, etc.)");
      return; // Empêche la soumission
    }

    // Validation stricte : politique d'annulation obligatoire
    if (!data.reservation_type) {
      toast.error("Vous devez sélectionner une politique d'annulation");
      return; // Empêche la soumission
    }

    // S'assurer que availableDays est un objet JSON
    const availableDays = {
      Lundi: true,
      Mardi: true,
      Mercredi: true,
      Jeudi: true,
      Vendredi: true,
      Samedi: true,
      Dimanche: true,
    };

    // Créer des prix basés sur les catégories activées
    let prices = getValues("prices") || [];

    // Si pas de prix mais des catégories activées, générer les prix
    if (prices.length === 0) {
      pricingCategories.forEach((category) => {
        if (category.enabled) {
          if (category.id === "halfday") {
            // Pour halfday, générer selon les options activées
            DAYS_OF_WEEK.forEach((day) => {
              if (halfdayOptions.morning) {
                prices.push({
                  price: 0,
                  min: 1,
                  max: (data as any)?.visitor || 50,
                  startHour: "08:00",
                  endHour: "14:00",
                  selectedDay: day.label,
                  type: "demi-journée",
                });
              }
              if (halfdayOptions.afternoon) {
                prices.push({
                  price: 0,
                  min: 1,
                  max: (data as any)?.visitor || 50,
                  startHour: "14:00",
                  endHour: "20:00",
                  selectedDay: day.label,
                  type: "demi-journée",
                });
              }
            });
          } else {
            // Pour les autres catégories
            const typeMap = {
              hourly: "heure",
              evening: "soirée",
              fullday: "journée complète",
            } as const;

            const targetType = typeMap[category.id as keyof typeof typeMap];
            if (targetType) {
              DAYS_OF_WEEK.forEach((day) => {
                let defaultStartHour = "09:00";
                let defaultEndHour = "18:00";

                if (category.id === "evening") {
                  defaultStartHour = "18:00";
                  defaultEndHour = "02:00";
                }

                prices.push({
                  price: 0,
                  min: 1,
                  max: (data as any)?.visitor || 50,
                  startHour: defaultStartHour,
                  endHour: defaultEndHour,
                  selectedDay: day.label,
                  type: targetType as "heure" | "soirée" | "journée complète",
                });
              });
            }
          }
        }
      });
    }

    // Filtrer les prix vides (ne garder que les prix > 0)
    const validPrices = prices.filter((p) => p.price && p.price > 0);

    const formDataToSend = {
      ...data,
      prices: validPrices,
      availableDays: availableDays,
    };

    FormData(formDataToSend);
  };

  const toggleCategoryEnabled = (categoryId: string) => {
    setPricingCategories((prev) => {
      const updated = prev.map((cat) =>
        cat.id === categoryId
          ? { ...cat, enabled: !cat.enabled, isConfigOpen: !cat.enabled ? false : cat.isConfigOpen }
          : cat
      );

      // Créer automatiquement les prix quand une catégorie est activée
      const enabledCategory = updated.find((cat) => cat.id === categoryId);
      if (enabledCategory?.enabled) {
        const currentPrices = getValues("prices") || [];
        const newPrices = [...currentPrices];

        // Pour halfday, créer les prix selon les toggles activés
        if (categoryId === "halfday") {
          DAYS_OF_WEEK.forEach((day) => {
            // Ajouter matin si activé
            if (halfdayOptions.morning) {
              const existsMorning = newPrices.some(
                (p) => p.selectedDay === day.label && p.type === "demi-journée" && p.startHour === "08:00"
              );
              if (!existsMorning) {
                newPrices.push({
                  price: 0,
                  min: 1,
                  max: (data as any)?.visitor || 50,
                  startHour: "08:00",
                  endHour: "14:00",
                  selectedDay: day.label,
                  type: "demi-journée",
                });
              }
            }

            // Ajouter après-midi si activé
            if (halfdayOptions.afternoon) {
              const existsAfternoon = newPrices.some(
                (p) => p.selectedDay === day.label && p.type === "demi-journée" && p.startHour === "14:00"
              );
              if (!existsAfternoon) {
                newPrices.push({
                  price: 0,
                  min: 1,
                  max: (data as any)?.visitor || 50,
                  startHour: "14:00",
                  endHour: "20:00",
                  selectedDay: day.label,
                  type: "demi-journée",
                });
              }
            }
          });
        } else {
          // Pour les autres catégories
          const typeMap = {
            hourly: "heure",
            evening: "soirée",
            fullday: "journée complète",
          } as const;

          DAYS_OF_WEEK.forEach((day) => {
            const targetType = typeMap[categoryId as keyof typeof typeMap];
            if (targetType) {
              const exists = newPrices.some((p) => p.selectedDay === day.label && p.type === targetType);

              if (!exists) {
                let defaultStartHour = "09:00";
                let defaultEndHour = "18:00";

                if (categoryId === "evening") {
                  defaultStartHour = "18:00";
                  defaultEndHour = "02:00";
                }

                newPrices.push({
                  price: 0,
                  min: 1,
                  max: (data as any)?.visitor || 50,
                  startHour: defaultStartHour,
                  endHour: defaultEndHour,
                  selectedDay: day.label,
                  type: targetType as "heure" | "soirée" | "journée complète",
                });
              }
            }
          });
        }

        setValue("prices", newPrices);
        console.log(`✅ Catégorie ${categoryId} activée - ${newPrices.length} prix générés`);
      } else {
        // Retirer les prix de cette catégorie quand elle est désactivée
        const currentPrices = getValues("prices") || [];
        let filteredPrices = [...currentPrices];

        if (categoryId === "halfday") {
          // Supprimer tous les prix de demi-journée
          filteredPrices = filteredPrices.filter((p) => p.type !== "demi-journée");
        } else {
          const typeMap = {
            hourly: "heure",
            evening: "soirée",
            fullday: "journée complète",
          } as const;

          const targetType = typeMap[categoryId as keyof typeof typeMap];
          if (targetType) {
            filteredPrices = filteredPrices.filter((p) => p.type !== targetType);
          }
        }

        setValue("prices", filteredPrices);
      }

      return updated;
    });
  };

  const toggleCategoryConfig = (categoryId: string) => {
    setPricingCategories((prev) =>
      prev.map((cat) => (cat.id === categoryId ? { ...cat, isConfigOpen: !cat.isConfigOpen } : cat))
    );
  };

  // Fonction pour appliquer la configuration d'un jour à tous les autres jours
  const applyToAllDays = (sourceDay: string, categoryId: string) => {
    const currentPrices = getValues("prices") || [];

    const typeMap = {
      hourly: "heure",
      evening: "soirée",
      fullday: "journée complète",
    } as const;

    const targetType = typeMap[categoryId as keyof typeof typeMap] as "heure" | "soirée" | "journée complète";

    // Trouver la configuration source
    const sourcePrice = currentPrices.find((p) => p.selectedDay === sourceDay && p.type === targetType);

    if (!sourcePrice) {
      toast.error(
        `Aucune configuration trouvée pour ${sourceDay} (${targetType}). Veuillez d'abord configurer ce jour.`
      );
      return;
    }

    // Appliquer à tous les autres jours
    const updatedPrices = [...currentPrices];

    DAYS_OF_WEEK.forEach((day) => {
      if (day.label !== sourceDay) {
        // Chercher si un prix existe déjà pour ce jour et ce type
        const existingIndex = updatedPrices.findIndex((p) => p.selectedDay === day.label && p.type === targetType);

        const newPrice = {
          ...sourcePrice,
          selectedDay: day.label,
        };

        if (existingIndex >= 0) {
          // Remplacer le prix existant
          updatedPrices[existingIndex] = newPrice;
        } else {
          // Ajouter un nouveau prix
          updatedPrices.push(newPrice);
        }
      }
    });

    // Mettre à jour le formulaire
    setValue("prices", updatedPrices);

    // Forcer le re-render des champs
    trigger("prices");
    setRefreshKey((prev) => prev + 1);

    toast.success(`Configuration de ${sourceDay} appliquée à tous les jours`);
  };

  // Fonctions pour gérer les créneaux multiples
  const getTimeSlotsForDay = (dayLabel: string, categoryId: string) => {
    const currentPrices = getValues("prices") || [];
    const typeMap = {
      hourly: "heure",
      evening: "soirée",
      fullday: "journée complète",
    } as const;
    const targetType = typeMap[categoryId as keyof typeof typeMap];

    return currentPrices.filter((p) => p.selectedDay === dayLabel && p.type === targetType);
  };

  const addTimeSlot = (dayLabel: string, categoryId: string) => {
    const currentPrices = getValues("prices") || [];
    const typeMap = {
      hourly: "heure",
      evening: "soirée",
      fullday: "journée complète",
    } as const;
    const targetType = typeMap[categoryId as keyof typeof typeMap];

    let defaultStartHour = "09:00";
    let defaultEndHour = "12:00";

    if (categoryId === "evening") {
      defaultStartHour = "18:00";
      defaultEndHour = "22:00";
    } else if (categoryId === "fullday") {
      defaultStartHour = "08:00";
      defaultEndHour = "22:00";
    }

    const newSlot = {
      price: 0,
      min: 1,
      max: (data as any)?.visitor || 50,
      startHour: defaultStartHour,
      endHour: defaultEndHour,
      selectedDay: dayLabel,
      type: targetType as "heure" | "soirée" | "journée complète",
    };

    currentPrices.push(newSlot);
    setValue("prices", currentPrices);
    trigger("prices");
    setRefreshKey((prev) => prev + 1);
  };

  const removeTimeSlot = (dayLabel: string, categoryId: string, slotIndex: number) => {
    const currentPrices = getValues("prices") || [];
    const typeMap = {
      hourly: "heure",
      evening: "soirée",
      fullday: "journée complète",
    } as const;
    const targetType = typeMap[categoryId as keyof typeof typeMap];

    const daySlots = currentPrices.filter((p) => p.selectedDay === dayLabel && p.type === targetType);
    const otherPrices = currentPrices.filter((p) => !(p.selectedDay === dayLabel && p.type === targetType));

    if (slotIndex >= 0 && slotIndex < daySlots.length) {
      daySlots.splice(slotIndex, 1);
      const updatedPrices = [...otherPrices, ...daySlots];
      setValue("prices", updatedPrices);
      trigger("prices");
      setRefreshKey((prev) => prev + 1);
    }
  };

  const updateTimeSlot = (dayLabel: string, categoryId: string, slotIndex: number, field: string, value: any) => {
    const currentPrices = getValues("prices") || [];
    const typeMap = {
      hourly: "heure",
      evening: "soirée",
      fullday: "journée complète",
    } as const;
    const targetType = typeMap[categoryId as keyof typeof typeMap];

    const priceIndex = currentPrices.findIndex((p, index) => {
      return (
        p.selectedDay === dayLabel &&
        p.type === targetType &&
        currentPrices.filter((pp) => pp.selectedDay === dayLabel && pp.type === targetType).indexOf(p) ===
          slotIndex
      );
    });

    if (priceIndex >= 0) {
      currentPrices[priceIndex] = {
        ...currentPrices[priceIndex],
        [field]: field === "price" || field === "min" || field === "max" ? Number(value) : value,
      };
      setValue("prices", currentPrices);
      trigger("prices");
    }
  };

  return (
    <Fragment>
      <form className="w-full" onSubmit={handleSubmit(onSubmit)}>
        <div className="w-full">
          <div>
            <h2 className="text-2xl font-semibold">Prix et disponibilité de votre espace</h2>
          </div>

          <div className="my-5">
            <FormItem label="Politique d'annulation *">
              <div className="flex space-x-4">
                <div className="relative flex-1">
                  <input
                    className="hidden peer"
                    id="radio_1"
                    type="radio"
                    {...register("reservation_type")}
                    value="flexible"
                  />
                  <span className="box-content absolute block w-3 h-3 -translate-y-1/2 bg-white border-8 border-gray-300 rounded-full right-4 top-1/2 peer-checked:border-indigo-500"></span>
                  <label
                    className="flex flex-col p-4 border border-gray-300 rounded-lg cursor-pointer peer-checked:border-4 peer-checked:border-indigo-700"
                    htmlFor="radio_1"
                  >
                    <span className="mt-2 text-xl font-bold">Flexible</span>
                  </label>
                </div>
                <div className="relative flex-1">
                  <input
                    className="hidden peer"
                    id="radio_2"
                    type="radio"
                    {...register("reservation_type")}
                    value="stricte"
                  />
                  <span className="box-content absolute block w-3 h-3 -translate-y-1/2 bg-white border-8 border-gray-300 rounded-full right-4 top-1/2 peer-checked:border-indigo-500"></span>
                  <label
                    className="flex flex-col p-4 border border-gray-300 rounded-lg cursor-pointer peer-checked:border-4 peer-checked:border-indigo-700"
                    htmlFor="radio_2"
                  >
                    <span className="mt-2 text-xl font-bold">Stricte</span>
                  </label>
                </div>
              </div>
              <ErrorMessage message={errors.reservation_type?.message || ""} />
            </FormItem>

            <div className="mt-10">
              <div className="flex items-center justify-between mb-4">
                <label className="font-semibold">Disponibilités et tarifs</label>
              </div>
              <span className="block mt-2 text-sm text-neutral-500 dark:text-neutral-400">
                Configurez vos différents types de tarification
              </span>

              <div className="space-y-4 mt-6">
                {pricingCategories.map((category) => {
                  const colors = getColorClasses(category.colorClass);
                  return (
                    <div
                      key={category.id}
                      className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <div
                            className={`w-12 h-12 ${colors.bgIcon} rounded-full flex items-center justify-center`}
                          >
                            <span className="text-xl">{category.emoji}</span>
                          </div>
                          <div>
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                              {category.title}
                            </h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400">{category.description}</p>
                          </div>
                        </div>

                        <div className="flex items-center space-x-4">
                          <div className="flex items-center space-x-2">
                            <span className="text-sm font-medium">Activer</span>
                            <Switch
                              checked={category.enabled}
                              onChange={() => toggleCategoryEnabled(category.id)}
                              disabled={isLoading}
                            />
                          </div>

                          <a
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              if (!category.enabled || isLoading) return;
                              toggleCategoryConfig(category.id);
                            }}
                            className={`${!category.enabled || isLoading ? "opacity-50 cursor-not-allowed pointer-events-none" : "text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"} text-sm font-medium transition-colors`}
                          >
                            {category.isConfigOpen ? "Fermer" : "Configurer"}
                          </a>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Configuration sections dynamiques */}
              {pricingCategories
                .filter((category) => category.isConfigOpen)
                .map((category) => (
                  <div
                    key={`config-${category.id}`}
                    className="mt-8 bg-gray-50 dark:bg-gray-900 rounded-xl p-6 border border-gray-200 dark:border-gray-700"
                  >
                    <div className="flex items-center justify-between mb-6">
                      <h4 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                        <span className="text-2xl mr-2">{category.emoji}</span>
                        Configuration - {category.title}
                      </h4>
                      <button
                        type="button"
                        onClick={() => toggleCategoryConfig(category.id)}
                        className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                      >
                        <span className="sr-only">Fermer</span>✕
                      </button>
                    </div>

                    {/* Section spéciale pour demi-journée */}
                    {category.id === "halfday" && (
                      <Fragment>
                        <div className="mb-6 p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                          <h5 className="text-md font-medium text-gray-900 dark:text-white mb-4">
                            Choisir les créneaux disponibles
                          </h5>
                          <div className="flex space-x-6">
                            <div className="flex items-center space-x-3">
                              <Switch
                                checked={halfdayOptions.morning}
                                onChange={(e) =>
                                  setHalfdayOptions((prev) => ({ ...prev, morning: e.target.checked }))
                                }
                                disabled={isLoading}
                              />
                              <div>
                                <div className="text-sm font-medium text-gray-900 dark:text-white">
                                  🌅 Matin (8h - 14h)
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                  6 heures de location
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center space-x-3">
                              <Switch
                                checked={halfdayOptions.afternoon}
                                onChange={(e) =>
                                  setHalfdayOptions((prev) => ({ ...prev, afternoon: e.target.checked }))
                                }
                                disabled={isLoading}
                              />
                              <div>
                                <div className="text-sm font-medium text-gray-900 dark:text-white">
                                  🌇 Après-midi (14h - 20h)
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                  6 heures de location
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="space-y-3">
                          {/* En-têtes des colonnes */}
                          <div className="grid grid-cols-7 gap-4 items-center px-4 py-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                            <div className="text-xs font-semibold text-gray-600 dark:text-gray-400">Jour</div>
                            <div className="text-xs font-semibold text-gray-600 dark:text-gray-400">
                              Heure début
                            </div>
                            <div className="text-xs font-semibold text-gray-600 dark:text-gray-400">Heure fin</div>
                            <div className="text-xs font-semibold text-gray-600 dark:text-gray-400">Prix (€)</div>
                            <div className="text-xs font-semibold text-gray-600 dark:text-gray-400">Capacité</div>
                            <div className="text-xs font-semibold text-gray-600 dark:text-gray-400">Activer</div>
                            <div className="text-xs font-semibold text-gray-600 dark:text-gray-400">Action</div>
                          </div>
                          {/* Jours - logique spéciale pour halfday */}
                          <div>
                            {/* Section jours de semaine */}
                            <div className="mb-6">
                              <h6 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center">
                                📅 Jours de semaine (Lundi - Vendredi)
                              </h6>
                              {DAYS_OF_WEEK.filter((day) => day.value !== "6" && day.value !== "0").map((day) => (
                                <div key={`${category.id}-${day.value}`}>
                                  {/* Ligne matin si activée */}
                                  {halfdayOptions.morning && (
                                    <div
                                      className="grid grid-cols-7 gap-4 items-center px-4 py-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors mb-2"
                                      data-halfday-type="morning"
                                      data-day={day.value}
                                    >
                                      <div className="font-medium text-gray-900 dark:text-white text-sm">
                                        {day.label} <span className="text-xs text-green-600">🌅 Matin</span>
                                      </div>
                                      <Select className="text-sm" defaultValue="08:00" data-field="start">
                                        <option value="08:00">08:00</option>
                                        <option value="09:00">09:00</option>
                                        <option value="10:00">10:00</option>
                                      </Select>
                                      <Select className="text-sm" defaultValue="14:00" data-field="end">
                                        <option value="13:00">13:00</option>
                                        <option value="14:00">14:00</option>
                                        <option value="15:00">15:00</option>
                                      </Select>
                                      <Input
                                        type="number"
                                        placeholder="Capacité"
                                        className="text-sm"
                                        data-field="capacity"
                                        max={(data as any)?.visitor || 100}
                                        value={(() => {
                                          const currentPrices = getValues("prices") || [];
                                          const priceObj = currentPrices.find(
                                            (p) =>
                                              p.selectedDay === day.label &&
                                              p.type === "demi-journée" &&
                                              p.startHour === "08:00"
                                          );
                                          return priceObj?.max || "";
                                        })()}
                                        onChange={(e) => {
                                          const newCapacity = Number(e.target.value);
                                          const currentPrices = getValues("prices") || [];
                                          const existingIndex = currentPrices.findIndex(
                                            (p) =>
                                              p.selectedDay === day.label &&
                                              p.type === "demi-journée" &&
                                              p.startHour === "08:00"
                                          );

                                          if (existingIndex >= 0) {
                                            currentPrices[existingIndex].max = newCapacity;
                                          } else {
                                            currentPrices.push({
                                              price: 0,
                                              min: 1,
                                              max: newCapacity,
                                              startHour: "08:00",
                                              endHour: "14:00",
                                              selectedDay: day.label,
                                              type: "demi-journée",
                                            });
                                          }
                                          setValue("prices", currentPrices);
                                          trigger("prices");
                                        }}
                                      />
                                      <Input
                                        type="number"
                                        placeholder="Prix"
                                        className="text-sm"
                                        data-field="price"
                                        value={(() => {
                                          const currentPrices = getValues("prices") || [];
                                          const priceObj = currentPrices.find(
                                            (p) =>
                                              p.selectedDay === day.label &&
                                              p.type === "demi-journée" &&
                                              p.startHour === "08:00"
                                          );
                                          return priceObj?.price ?? "";
                                        })()}
                                        onChange={(e) => {
                                          const newPrice = Number(e.target.value);
                                          const currentPrices = getValues("prices") || [];
                                          const existingIndex = currentPrices.findIndex(
                                            (p) =>
                                              p.selectedDay === day.label &&
                                              p.type === "demi-journée" &&
                                              p.startHour === "08:00"
                                          );

                                          if (existingIndex >= 0) {
                                            currentPrices[existingIndex].price = newPrice;
                                          } else {
                                            currentPrices.push({
                                              price: newPrice,
                                              min: 1,
                                              max: 50,
                                              startHour: "08:00",
                                              endHour: "14:00",
                                              selectedDay: day.label,
                                              type: "demi-journée",
                                            });
                                          }
                                          setValue("prices", currentPrices);
                                          trigger("prices");
                                        }}
                                      />
                                      <Switch
                                        data-field="enabled"
                                        checked={(() => {
                                          const currentPrices = getValues("prices") || [];
                                          const priceObj = currentPrices.find(
                                            (p) =>
                                              p.selectedDay === day.label &&
                                              p.type === "demi-journée" &&
                                              p.startHour === "08:00"
                                          );
                                          return !!priceObj;
                                        })()}
                                        onChange={(checked) => {
                                          const currentPrices = getValues("prices") || [];
                                          const existingIndex = currentPrices.findIndex(
                                            (p) =>
                                              p.selectedDay === day.label &&
                                              p.type === "demi-journée" &&
                                              p.startHour === "08:00"
                                          );

                                          if (checked && existingIndex < 0) {
                                            currentPrices.push({
                                              price: 0,
                                              min: 1,
                                              max: 50,
                                              startHour: "08:00",
                                              endHour: "14:00",
                                              selectedDay: day.label,
                                              type: "demi-journée",
                                            });
                                          } else if (!checked && existingIndex >= 0) {
                                            currentPrices.splice(existingIndex, 1);
                                          }
                                          setValue("prices", currentPrices);
                                          trigger("prices");
                                        }}
                                      />
                                      <button
                                        type="button"
                                        onClick={() => applyToAllMorning(day.value)}
                                        className="text-blue-600 hover:text-blue-800 text-sm"
                                      >
                                        Appliquer partout
                                      </button>
                                    </div>
                                  )}
                                  {/* Ligne après-midi si activée */}
                                  {halfdayOptions.afternoon && (
                                    <div
                                      className="grid grid-cols-7 gap-4 items-center px-4 py-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors mb-2"
                                      data-halfday-type="afternoon"
                                      data-day={day.value}
                                    >
                                      <div className="font-medium text-gray-900 dark:text-white text-sm">
                                        {day.label} <span className="text-xs text-orange-600">🌇 A-midi</span>
                                      </div>
                                      <Select className="text-sm" defaultValue="14:00" data-field="start">
                                        <option value="13:00">13:00</option>
                                        <option value="14:00">14:00</option>
                                        <option value="15:00">15:00</option>
                                      </Select>
                                      <Select className="text-sm" defaultValue="20:00" data-field="end">
                                        <option value="19:00">19:00</option>
                                        <option value="20:00">20:00</option>
                                        <option value="21:00">21:00</option>
                                      </Select>
                                      <Input
                                        type="number"
                                        placeholder="Capacité"
                                        className="text-sm"
                                        data-field="capacity"
                                        max={(data as any)?.visitor || 100}
                                        value={(() => {
                                          const currentPrices = getValues("prices") || [];
                                          const priceObj = currentPrices.find(
                                            (p) =>
                                              p.selectedDay === day.label &&
                                              p.type === "demi-journée" &&
                                              p.startHour === "14:00"
                                          );
                                          return priceObj?.max || "";
                                        })()}
                                        onChange={(e) => {
                                          const newCapacity = Number(e.target.value);
                                          const currentPrices = getValues("prices") || [];
                                          const existingIndex = currentPrices.findIndex(
                                            (p) =>
                                              p.selectedDay === day.label &&
                                              p.type === "demi-journée" &&
                                              p.startHour === "14:00"
                                          );

                                          if (existingIndex >= 0) {
                                            currentPrices[existingIndex].max = newCapacity;
                                          } else {
                                            currentPrices.push({
                                              price: 0,
                                              min: 1,
                                              max: newCapacity,
                                              startHour: "14:00",
                                              endHour: "20:00",
                                              selectedDay: day.label,
                                              type: "demi-journée",
                                            });
                                          }
                                          setValue("prices", currentPrices);
                                          trigger("prices");
                                        }}
                                      />
                                      <Input
                                        type="number"
                                        placeholder="Prix"
                                        className="text-sm"
                                        data-field="price"
                                        value={(() => {
                                          const currentPrices = getValues("prices") || [];
                                          const priceObj = currentPrices.find(
                                            (p) =>
                                              p.selectedDay === day.label &&
                                              p.type === "demi-journée" &&
                                              p.startHour === "14:00"
                                          );
                                          return priceObj?.price ?? "";
                                        })()}
                                        onChange={(e) => {
                                          const newPrice = Number(e.target.value);
                                          const currentPrices = getValues("prices") || [];
                                          const existingIndex = currentPrices.findIndex(
                                            (p) =>
                                              p.selectedDay === day.label &&
                                              p.type === "demi-journée" &&
                                              p.startHour === "14:00"
                                          );

                                          if (existingIndex >= 0) {
                                            currentPrices[existingIndex].price = newPrice;
                                          } else {
                                            currentPrices.push({
                                              price: newPrice,
                                              min: 1,
                                              max: 50,
                                              startHour: "14:00",
                                              endHour: "20:00",
                                              selectedDay: day.label,
                                              type: "demi-journée",
                                            });
                                          }
                                          setValue("prices", currentPrices);
                                          trigger("prices");
                                        }}
                                      />
                                      <Switch
                                        data-field="enabled"
                                        checked={(() => {
                                          const currentPrices = getValues("prices") || [];
                                          const priceObj = currentPrices.find(
                                            (p) =>
                                              p.selectedDay === day.label &&
                                              p.type === "demi-journée" &&
                                              p.startHour === "14:00"
                                          );
                                          return !!priceObj;
                                        })()}
                                        onChange={(checked) => {
                                          const currentPrices = getValues("prices") || [];
                                          const existingIndex = currentPrices.findIndex(
                                            (p) =>
                                              p.selectedDay === day.label &&
                                              p.type === "demi-journée" &&
                                              p.startHour === "14:00"
                                          );

                                          if (checked && existingIndex < 0) {
                                            currentPrices.push({
                                              price: 0,
                                              min: 1,
                                              max: 50,
                                              startHour: "14:00",
                                              endHour: "20:00",
                                              selectedDay: day.label,
                                              type: "demi-journée",
                                            });
                                          } else if (!checked && existingIndex >= 0) {
                                            currentPrices.splice(existingIndex, 1);
                                          }
                                          setValue("prices", currentPrices);
                                          trigger("prices");
                                        }}
                                      />
                                      <button
                                        type="button"
                                        onClick={() => applyToAllAfternoon(day.value)}
                                        className="text-blue-600 hover:text-blue-800 text-sm"
                                      >
                                        Appliquer partout
                                      </button>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>

                            {/* Section Weekend */}
                            <div className="mb-6">
                              <h6 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center">
                                🏖️ Weekend (Samedi - Dimanche)
                              </h6>
                              {DAYS_OF_WEEK.filter((day) => day.value === "6" || day.value === "0").map((day) => (
                                <div key={`${category.id}-weekend-${day.value}`}>
                                  {/* Ligne matin si activée */}
                                  {halfdayOptions.morning && (
                                    <div
                                      className="grid grid-cols-7 gap-4 items-center px-4 py-3 bg-blue-50 dark:bg-blue-900/30 rounded-lg border border-blue-200 dark:border-blue-700 hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors mb-2"
                                      data-halfday-type="morning"
                                      data-day={day.value}
                                    >
                                      <div className="font-medium text-gray-900 dark:text-white text-sm">
                                        {day.label} <span className="text-xs text-green-600">🌅 Matin</span>
                                      </div>
                                      <Select className="text-sm" defaultValue="08:00" data-field="start">
                                        <option value="08:00">08:00</option>
                                        <option value="09:00">09:00</option>
                                        <option value="10:00">10:00</option>
                                      </Select>
                                      <Select className="text-sm" defaultValue="14:00" data-field="end">
                                        <option value="13:00">13:00</option>
                                        <option value="14:00">14:00</option>
                                        <option value="15:00">15:00</option>
                                      </Select>
                                      <Input
                                        type="number"
                                        placeholder="Capacité"
                                        className="text-sm"
                                        data-field="capacity"
                                        max={(data as any)?.visitor || 100}
                                        value={(() => {
                                          const currentPrices = getValues("prices") || [];
                                          const priceObj = currentPrices.find(
                                            (p) =>
                                              p.selectedDay === day.label &&
                                              p.type === "demi-journée" &&
                                              p.startHour === "08:00"
                                          );
                                          return priceObj?.max || "";
                                        })()}
                                        onChange={(e) => {
                                          const newCapacity = Number(e.target.value);
                                          const currentPrices = getValues("prices") || [];
                                          const existingIndex = currentPrices.findIndex(
                                            (p) =>
                                              p.selectedDay === day.label &&
                                              p.type === "demi-journée" &&
                                              p.startHour === "08:00"
                                          );

                                          if (existingIndex >= 0) {
                                            currentPrices[existingIndex].max = newCapacity;
                                          } else {
                                            currentPrices.push({
                                              price: 0,
                                              min: 1,
                                              max: newCapacity,
                                              startHour: "08:00",
                                              endHour: "14:00",
                                              selectedDay: day.label,
                                              type: "demi-journée",
                                            });
                                          }
                                          setValue("prices", currentPrices);
                                          trigger("prices");
                                        }}
                                      />
                                      <Input
                                        type="number"
                                        placeholder="Prix"
                                        className="text-sm"
                                        data-field="price"
                                        value={(() => {
                                          const currentPrices = getValues("prices") || [];
                                          const priceObj = currentPrices.find(
                                            (p) =>
                                              p.selectedDay === day.label &&
                                              p.type === "demi-journée" &&
                                              p.startHour === "08:00"
                                          );
                                          return priceObj?.price ?? "";
                                        })()}
                                        onChange={(e) => {
                                          const newPrice = Number(e.target.value);
                                          const currentPrices = getValues("prices") || [];
                                          const existingIndex = currentPrices.findIndex(
                                            (p) =>
                                              p.selectedDay === day.label &&
                                              p.type === "demi-journée" &&
                                              p.startHour === "08:00"
                                          );

                                          if (existingIndex >= 0) {
                                            currentPrices[existingIndex].price = newPrice;
                                          } else {
                                            currentPrices.push({
                                              price: newPrice,
                                              min: 1,
                                              max: 50,
                                              startHour: "08:00",
                                              endHour: "14:00",
                                              selectedDay: day.label,
                                              type: "demi-journée",
                                            });
                                          }
                                          setValue("prices", currentPrices);
                                          trigger("prices");
                                        }}
                                      />
                                      <Switch
                                        data-field="enabled"
                                        checked={(() => {
                                          const currentPrices = getValues("prices") || [];
                                          const priceObj = currentPrices.find(
                                            (p) =>
                                              p.selectedDay === day.label &&
                                              p.type === "demi-journée" &&
                                              p.startHour === "08:00"
                                          );
                                          return !!priceObj;
                                        })()}
                                        onChange={(checked) => {
                                          const currentPrices = getValues("prices") || [];
                                          const existingIndex = currentPrices.findIndex(
                                            (p) =>
                                              p.selectedDay === day.label &&
                                              p.type === "demi-journée" &&
                                              p.startHour === "08:00"
                                          );

                                          if (checked && existingIndex < 0) {
                                            currentPrices.push({
                                              price: 0,
                                              min: 1,
                                              max: 50,
                                              startHour: "08:00",
                                              endHour: "14:00",
                                              selectedDay: day.label,
                                              type: "demi-journée",
                                            });
                                          } else if (!checked && existingIndex >= 0) {
                                            currentPrices.splice(existingIndex, 1);
                                          }
                                          setValue("prices", currentPrices);
                                          trigger("prices");
                                        }}
                                      />
                                      <button
                                        type="button"
                                        onClick={() => applyToAllMorning(day.value)}
                                        className="text-blue-600 hover:text-blue-800 text-sm"
                                      >
                                        Appliquer partout
                                      </button>
                                    </div>
                                  )}
                                  {/* Ligne après-midi si activée */}
                                  {halfdayOptions.afternoon && (
                                    <div
                                      className="grid grid-cols-7 gap-4 items-center px-4 py-3 bg-orange-50 dark:bg-orange-900/30 rounded-lg border border-orange-200 dark:border-orange-700 hover:bg-orange-100 dark:hover:bg-orange-900/50 transition-colors mb-2"
                                      data-halfday-type="afternoon"
                                      data-day={day.value}
                                    >
                                      <div className="font-medium text-gray-900 dark:text-white text-sm">
                                        {day.label} <span className="text-xs text-orange-600">🌇 A-midi</span>
                                      </div>
                                      <Select className="text-sm" defaultValue="14:00" data-field="start">
                                        <option value="13:00">13:00</option>
                                        <option value="14:00">14:00</option>
                                        <option value="15:00">15:00</option>
                                      </Select>
                                      <Select className="text-sm" defaultValue="20:00" data-field="end">
                                        <option value="19:00">19:00</option>
                                        <option value="20:00">20:00</option>
                                        <option value="21:00">21:00</option>
                                      </Select>
                                      <Input
                                        type="number"
                                        placeholder="Capacité"
                                        className="text-sm"
                                        data-field="capacity"
                                        max={(data as any)?.visitor || 100}
                                        value={(() => {
                                          const currentPrices = getValues("prices") || [];
                                          const priceObj = currentPrices.find(
                                            (p) =>
                                              p.selectedDay === day.label &&
                                              p.type === "demi-journée" &&
                                              p.startHour === "14:00"
                                          );
                                          return priceObj?.max || "";
                                        })()}
                                        onChange={(e) => {
                                          const newCapacity = Number(e.target.value);
                                          const currentPrices = getValues("prices") || [];
                                          const existingIndex = currentPrices.findIndex(
                                            (p) =>
                                              p.selectedDay === day.label &&
                                              p.type === "demi-journée" &&
                                              p.startHour === "14:00"
                                          );

                                          if (existingIndex >= 0) {
                                            currentPrices[existingIndex].max = newCapacity;
                                          } else {
                                            currentPrices.push({
                                              price: 0,
                                              min: 1,
                                              max: newCapacity,
                                              startHour: "14:00",
                                              endHour: "20:00",
                                              selectedDay: day.label,
                                              type: "demi-journée",
                                            });
                                          }
                                          setValue("prices", currentPrices);
                                          trigger("prices");
                                        }}
                                      />
                                      <Input
                                        type="number"
                                        placeholder="Prix"
                                        className="text-sm"
                                        data-field="price"
                                        value={(() => {
                                          const currentPrices = getValues("prices") || [];
                                          const priceObj = currentPrices.find(
                                            (p) =>
                                              p.selectedDay === day.label &&
                                              p.type === "demi-journée" &&
                                              p.startHour === "14:00"
                                          );
                                          return priceObj?.price ?? "";
                                        })()}
                                        onChange={(e) => {
                                          const newPrice = Number(e.target.value);
                                          const currentPrices = getValues("prices") || [];
                                          const existingIndex = currentPrices.findIndex(
                                            (p) =>
                                              p.selectedDay === day.label &&
                                              p.type === "demi-journée" &&
                                              p.startHour === "14:00"
                                          );

                                          if (existingIndex >= 0) {
                                            currentPrices[existingIndex].price = newPrice;
                                          } else {
                                            currentPrices.push({
                                              price: newPrice,
                                              min: 1,
                                              max: 50,
                                              startHour: "14:00",
                                              endHour: "20:00",
                                              selectedDay: day.label,
                                              type: "demi-journée",
                                            });
                                          }
                                          setValue("prices", currentPrices);
                                          trigger("prices");
                                        }}
                                      />
                                      <Switch
                                        data-field="enabled"
                                        checked={(() => {
                                          const currentPrices = getValues("prices") || [];
                                          const priceObj = currentPrices.find(
                                            (p) =>
                                              p.selectedDay === day.label &&
                                              p.type === "demi-journée" &&
                                              p.startHour === "14:00"
                                          );
                                          return !!priceObj;
                                        })()}
                                        onChange={(checked) => {
                                          const currentPrices = getValues("prices") || [];
                                          const existingIndex = currentPrices.findIndex(
                                            (p) =>
                                              p.selectedDay === day.label &&
                                              p.type === "demi-journée" &&
                                              p.startHour === "14:00"
                                          );

                                          if (checked && existingIndex < 0) {
                                            currentPrices.push({
                                              price: 0,
                                              min: 1,
                                              max: 50,
                                              startHour: "14:00",
                                              endHour: "20:00",
                                              selectedDay: day.label,
                                              type: "demi-journée",
                                            });
                                          } else if (!checked && existingIndex >= 0) {
                                            currentPrices.splice(existingIndex, 1);
                                          }
                                          setValue("prices", currentPrices);
                                          trigger("prices");
                                        }}
                                      />
                                      <button
                                        type="button"
                                        onClick={() => applyToAllAfternoon(day.value)}
                                        className="text-blue-600 hover:text-blue-800 text-sm"
                                      >
                                        Appliquer partout
                                      </button>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </Fragment>
                    )}

                    {/* Pour les autres catégories, logique avec créneaux multiples */}
                    {category.id !== "halfday" && (
                      <div>
                        {/* Section jours de semaine */}
                        <div className="mb-6">
                          <h6 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center">
                            📅 Jours de semaine (Lundi - Vendredi)
                          </h6>
                          {DAYS_OF_WEEK.filter((day) => day.value !== "6" && day.value !== "0").map((day) => {
                            const timeSlots = getTimeSlotsForDay(day.label, category.id);

                            return (
                              <div key={`${category.id}-${day.value}`} className="mb-4">
                                {/* Header du jour */}
                                <div className="flex items-center justify-between mb-3 px-4 py-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600">
                                  <div className="flex items-center space-x-3">
                                    <div className="font-semibold text-gray-900 dark:text-white">
                                      📅 {day.label}
                                    </div>
                                    <div className="text-sm text-gray-500 dark:text-gray-400">
                                      {timeSlots.length} créneau{timeSlots.length !== 1 ? "x" : ""}
                                    </div>
                                  </div>
                                  <div className="flex items-center space-x-2">
                                    <button
                                      type="button"
                                      onClick={() => addTimeSlot(day.label, category.id)}
                                      className="flex items-center space-x-1 px-3 py-1 text-xs bg-blue-500 hover:bg-blue-600 text-white rounded-md transition-colors"
                                    >
                                      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                        <path
                                          fillRule="evenodd"
                                          d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                                          clipRule="evenodd"
                                        />
                                      </svg>
                                      <span>Ajouter</span>
                                    </button>
                                    {timeSlots.length > 0 && (
                                      <button
                                        type="button"
                                        onClick={() => applyToAllDays(day.label, category.id)}
                                        className="px-3 py-1 text-xs bg-green-500 hover:bg-green-600 text-white rounded-md transition-colors"
                                      >
                                        Appliquer partout
                                      </button>
                                    )}
                                  </div>
                                </div>

                                {/* Créneaux existants */}
                                {timeSlots.length === 0 ? (
                                  <div className="text-center py-6 text-gray-500 dark:text-gray-400 text-sm">
                                    Aucun créneau configuré. Cliquez sur "Ajouter" pour créer votre premier
                                    créneau.
                                  </div>
                                ) : (
                                  <div className="space-y-2">
                                    {timeSlots.map((slot, slotIndex) => (
                                      <div
                                        key={`${day.label}-${category.id}-${slotIndex}`}
                                        className="grid grid-cols-6 gap-3 items-center px-4 py-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                                      >
                                        {/* Heure de début */}
                                        <select
                                          className="text-sm px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                          value={slot.startHour}
                                          onChange={(e) =>
                                            updateTimeSlot(
                                              day.label,
                                              category.id,
                                              slotIndex,
                                              "startHour",
                                              e.target.value
                                            )
                                          }
                                        >
                                          {/* Heures de 08:00 à 23:00, puis 00:00 */}
                                          {[...Array.from({ length: 16 }, (_, i) => i + 8), 0].map((hour) => {
                                            const hourStr = hour.toString().padStart(2, "0");
                                            return (
                                              <option key={hour} value={`${hourStr}:00`}>
                                                {hourStr}:00
                                              </option>
                                            );
                                          })}
                                        </select>

                                        {/* Heure de fin */}
                                        <select
                                          className="text-sm px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                          value={slot.endHour}
                                          onChange={(e) =>
                                            updateTimeSlot(
                                              day.label,
                                              category.id,
                                              slotIndex,
                                              "endHour",
                                              e.target.value
                                            )
                                          }
                                        >
                                          {/* Heures de 08:00 à 23:00, puis 00:00 */}
                                          {[...Array.from({ length: 16 }, (_, i) => i + 8), 0].map((hour) => {
                                            const hourStr = hour.toString().padStart(2, "0");
                                            return (
                                              <option key={hour} value={`${hourStr}:00`}>
                                                {hourStr}:00
                                              </option>
                                            );
                                          })}
                                        </select>

                                        {/* Capacité */}
                                        <input
                                          type="number"
                                          placeholder="Capacité"
                                          className="text-sm px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                          max={(data as any)?.visitor || 100}
                                          value={slot.max || ""}
                                          onChange={(e) =>
                                            updateTimeSlot(
                                              day.label,
                                              category.id,
                                              slotIndex,
                                              "max",
                                              e.target.value
                                            )
                                          }
                                        />

                                        {/* Prix */}
                                        <input
                                          type="number"
                                          placeholder="Prix"
                                          className="text-sm px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                          value={slot.price || ""}
                                          onChange={(e) =>
                                            updateTimeSlot(
                                              day.label,
                                              category.id,
                                              slotIndex,
                                              "price",
                                              e.target.value
                                            )
                                          }
                                        />

                                        {/* Unité */}
                                        <div className="text-xs text-gray-500 dark:text-gray-400 text-center">
                                          {category.id === "hourly" && "€/heure"}
                                          {category.id === "evening" && "€/soirée"}
                                          {category.id === "fullday" && "€/jour"}
                                        </div>

                                        {/* Bouton supprimer */}
                                        <button
                                          type="button"
                                          onClick={() => removeTimeSlot(day.label, category.id, slotIndex)}
                                          className="flex items-center justify-center w-8 h-8 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                                          title="Supprimer ce créneau"
                                        >
                                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                            <path
                                              fillRule="evenodd"
                                              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                              clipRule="evenodd"
                                            />
                                          </svg>
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>

                        {/* Section week-end */}
                        <div className="mb-6">
                          <h6 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center">
                            🏖️ Weekend (Samedi - Dimanche)
                          </h6>
                          {DAYS_OF_WEEK.filter((day) => day.value === "6" || day.value === "0").map((day) => {
                            const timeSlots = getTimeSlotsForDay(day.label, category.id);

                            return (
                              <div key={`${category.id}-${day.value}`} className="mb-4">
                                {/* Header du jour */}
                                <div className="flex items-center justify-between mb-3 px-4 py-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-700">
                                  <div className="flex items-center space-x-3">
                                    <div className="font-semibold text-gray-900 dark:text-white">
                                      🏖️ {day.label}
                                    </div>
                                    <div className="text-sm text-blue-600 dark:text-blue-400">
                                      {timeSlots.length} créneau{timeSlots.length !== 1 ? "x" : ""}
                                    </div>
                                  </div>
                                  <div className="flex items-center space-x-2">
                                    <button
                                      type="button"
                                      onClick={() => addTimeSlot(day.label, category.id)}
                                      className="flex items-center space-x-1 px-3 py-1 text-xs bg-blue-500 hover:bg-blue-600 text-white rounded-md transition-colors"
                                    >
                                      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                        <path
                                          fillRule="evenodd"
                                          d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                                          clipRule="evenodd"
                                        />
                                      </svg>
                                      <span>Ajouter</span>
                                    </button>
                                    {timeSlots.length > 0 && (
                                      <button
                                        type="button"
                                        onClick={() => applyToAllDays(day.label, category.id)}
                                        className="px-3 py-1 text-xs bg-green-500 hover:bg-green-600 text-white rounded-md transition-colors"
                                      >
                                        Appliquer partout
                                      </button>
                                    )}
                                  </div>
                                </div>

                                {/* Créneaux existants */}
                                {timeSlots.length === 0 ? (
                                  <div className="text-center py-6 text-gray-500 dark:text-gray-400 text-sm">
                                    Aucun créneau configuré. Cliquez sur "Ajouter" pour créer votre premier
                                    créneau.
                                  </div>
                                ) : (
                                  <div className="space-y-2">
                                    {timeSlots.map((slot, slotIndex) => (
                                      <div
                                        key={`${day.label}-${category.id}-${slotIndex}`}
                                        className="grid grid-cols-6 gap-3 items-center px-4 py-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                                      >
                                        {/* Heure de début */}
                                        <select
                                          className="text-sm px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                          value={slot.startHour}
                                          onChange={(e) =>
                                            updateTimeSlot(
                                              day.label,
                                              category.id,
                                              slotIndex,
                                              "startHour",
                                              e.target.value
                                            )
                                          }
                                        >
                                          {/* Heures de 08:00 à 23:00, puis 00:00 */}
                                          {[...Array.from({ length: 16 }, (_, i) => i + 8), 0].map((hour) => {
                                            const hourStr = hour.toString().padStart(2, "0");
                                            return (
                                              <option key={hour} value={`${hourStr}:00`}>
                                                {hourStr}:00
                                              </option>
                                            );
                                          })}
                                        </select>

                                        {/* Heure de fin */}
                                        <select
                                          className="text-sm px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                          value={slot.endHour}
                                          onChange={(e) =>
                                            updateTimeSlot(
                                              day.label,
                                              category.id,
                                              slotIndex,
                                              "endHour",
                                              e.target.value
                                            )
                                          }
                                        >
                                          {/* Heures de 08:00 à 23:00, puis 00:00 */}
                                          {[...Array.from({ length: 16 }, (_, i) => i + 8), 0].map((hour) => {
                                            const hourStr = hour.toString().padStart(2, "0");
                                            return (
                                              <option key={hour} value={`${hourStr}:00`}>
                                                {hourStr}:00
                                              </option>
                                            );
                                          })}
                                        </select>

                                        {/* Capacité */}
                                        <input
                                          type="number"
                                          placeholder="Capacité"
                                          className="text-sm px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                          max={(data as any)?.visitor || 100}
                                          value={slot.max || ""}
                                          onChange={(e) =>
                                            updateTimeSlot(
                                              day.label,
                                              category.id,
                                              slotIndex,
                                              "max",
                                              e.target.value
                                            )
                                          }
                                        />

                                        {/* Prix */}
                                        <input
                                          type="number"
                                          placeholder="Prix"
                                          className="text-sm px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                          value={slot.price || ""}
                                          onChange={(e) =>
                                            updateTimeSlot(
                                              day.label,
                                              category.id,
                                              slotIndex,
                                              "price",
                                              e.target.value
                                            )
                                          }
                                        />

                                        {/* Unité */}
                                        <div className="text-xs text-gray-500 dark:text-gray-400 text-center">
                                          {category.id === "hourly" && "€/heure"}
                                          {category.id === "evening" && "€/soirée"}
                                          {category.id === "fullday" && "€/jour"}
                                        </div>

                                        {/* Bouton supprimer */}
                                        <button
                                          type="button"
                                          onClick={() => removeTimeSlot(day.label, category.id, slotIndex)}
                                          className="flex items-center justify-center w-8 h-8 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                                          title="Supprimer ce créneau"
                                        >
                                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                            <path
                                              fillRule="evenodd"
                                              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                              clipRule="evenodd"
                                            />
                                          </svg>
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
            </div>

            {/* Frais et garanties */}
            <div className="mt-8 p-6 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
              <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                💰 Frais supplémentaires
              </h4>

              <div className="flex justify-between w-full space-x-5">
                <FormItem label="Frais de ménage (€)" className="mt-3">
                  <Input
                    type="number"
                    {...register("cleaning_costs", { valueAsNumber: true })}
                    placeholder="Frais de ménage"
                    className={errors.cleaning_costs?.message ? "border-red-500" : ""}
                    disabled={isLoading}
                  />
                  <ErrorMessage message={errors.cleaning_costs?.message || ""} />
                </FormItem>

                <FormItem label="Montant du dépôt de garantie (€) *" className="mt-3">
                  <Input
                    type="number"
                    {...register("guarantee", { valueAsNumber: true })}
                    placeholder="Montant du dépôt de garantie"
                    className={errors.guarantee?.message ? "border-red-500" : ""}
                    disabled={isLoading}
                  />
                  <ErrorMessage message={errors.guarantee?.message || ""} />
                </FormItem>
              </div>
            </div>
          </div>
        </div>
      </form>
    </Fragment>
  );
};

export default LocationAvailibility;
